import { layoutOf } from '@ayumia/component-runtime';
import { text } from '../../app/i18n';
import type { HistoryPort, VersionRow } from '../editor/canvas-types';
import { diffHeadline, semanticDiff, type DiffItem } from '../editor/semantic-diff';
import { ORIGIN_LABELS } from './forms-api';
import type { Template } from './layout-model';

/**
 * Historique du profil « formulaire » (ADR-146 §5.3) : versions du gabarit et gabarit du Pack d'origine,
 * comparés au brouillon en mots (« +2 blocs, −1 champ, 3 réglages modifiés »), restaurés dans le brouillon
 * seulement : rien n'est publié sans « Proposer » puis l'accord d'une autre personne.
 */

const fr = (label: unknown, fallback: string): string => text(label as Record<string, string> | undefined, 'fr', fallback);

/** Un gabarit découpé en éléments nommés : parties, champs, pages, blocs, totaux et réglages du formulaire. */
export function templateItems(template: Template): DiffItem[] {
  const items: DiffItem[] = [];
  for (const section of template.sections) {
    const { fields, ...rest } = section;
    const sectionLabel = fr(section.label, 'Partie');
    items.push({ key: section.section_id, kind: 'section', label: sectionLabel, value: { ...rest, repeat: section.repeat === undefined ? undefined : { ...section.repeat, children: undefined } } });
    for (const entry of [...fields, ...(section.repeat?.children?.fields ?? [])]) items.push({ key: `${section.section_id}.${entry.field.field_id}`, kind: 'field', label: fr(entry.field.label, 'Champ'), where: `dans « ${sectionLabel} »`, value: entry });
  }
  const layout = layoutOf(template);
  for (const page of layout?.pages ?? []) items.push({ key: page.page_id, kind: 'page', label: fr(page.label, 'Page'), value: page });
  for (const block of layout?.blocks ?? []) items.push({ key: block.block_id, kind: 'block', label: fr(block.label, 'Bloc'), value: block });
  for (const aggregate of layout?.aggregates ?? []) items.push({ key: aggregate.aggregate_id, kind: 'total', label: fr(aggregate.label, 'Total'), value: aggregate });
  items.push({ key: 'form', kind: 'setting', label: 'Formulaire', value: { label: template.label, description: template.description, layout: layout === undefined ? undefined : { ...layout, pages: undefined, blocks: undefined, aggregates: undefined, placements: undefined }, placements: layout?.placements } });
  return items;
}

const NOUNS = { section: 'Partie', field: 'Champ', page: 'Page', block: 'Bloc', total: 'Total', setting: 'Réglages du' };
const PLURALS = { section: ['partie', 'parties'], field: ['champ', 'champs'], page: ['page', 'pages'], block: ['bloc', 'blocs'], total: ['total', 'totaux'], setting: ['réglage', 'réglages'] } as const;

export function compareTemplates(from: Template, to: Template): { headline: string; lines: ReturnType<typeof semanticDiff>['lines'] } {
  const diff = semanticDiff(templateItems(from), templateItems(to), NOUNS);
  return { headline: diffHeadline(diff, PLURALS), lines: diff.lines };
}

export interface TemplateVersionRow {
  artifact_id: string;
  artifact_version: string;
  status: string;
  origin: string;
  at?: string;
  is_origin: boolean;
}

const STATUS: Readonly<Record<string, string>> = { published: 'publié', deprecated: 'ancien', draft: 'brouillon', sandbox: 'en essai', testing: 'en essai', validated: 'validé', retired: 'retiré' };

const dateOf = (at: string | undefined): string => (at === undefined ? '' : new Date(at).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit', year: 'numeric' }));

/** Lignes de l'historique, en mots : couche, version, date ; le Pack d'origine étiqueté comme tel. */
export function versionRows(rows: readonly TemplateVersionRow[]): VersionRow[] {
  return rows.map((row) => ({
    id: `${row.artifact_id}@${row.artifact_version}`,
    name: `${ORIGIN_LABELS[row.origin] ?? 'Couche'} ${row.artifact_version}`,
    meta: row.is_origin ? `gabarit d’origine, jamais modifié${row.at === undefined ? '' : ` · ${dateOf(row.at)}`}` : `${STATUS[row.status] ?? row.status}${row.at === undefined ? '' : ` · ${dateOf(row.at)}`}`,
    tag: row.is_origin ? ORIGIN_LABELS[row.origin] ?? 'origine' : STATUS[row.status] ?? row.status,
    tone: row.is_origin ? 'origin' : row.status === 'published' || row.status === 'deprecated' ? 'published' : 'draft',
  }));
}

export function formHistoryPort(source: { versions: () => Promise<TemplateVersionRow[]>; body: (artifactId: string, version: string) => Promise<Template> }): HistoryPort<Template> {
  let rows: TemplateVersionRow[] = [];
  return {
    versions: async () => {
      rows = await source.versions();
      return versionRows(rows);
    },
    load: async (row) => {
      const found = rows.find((candidate) => `${candidate.artifact_id}@${candidate.artifact_version}` === row.id);
      if (found === undefined) throw new Error('Version introuvable.');
      return source.body(found.artifact_id, found.artifact_version);
    },
    compare: compareTemplates,
  };
}
